import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { getNotes as getChordNotes } from '../chord/get-notes.js'
import { NoteLib } from '../note/index.js'
import ScaleLib from '../scale/scale.js'

/**
 * Per-chord modal-context entry emitted by `detectModalSignature`. One entry
 * per chord in the input window that contains the mode's characteristic note
 * — the UI glows each independently (Joni's UX spec §2.3).
 *
 * `chordIndex` is the position into the input `chords` array, so the consumer
 * (per-chord SimpleChord wrapper) can route the entry to the correct chip.
 */
export interface ModalSignatureHit {
  mode: 'dorian' | 'mixolydian' | 'phrygian' | 'lydian'
  reason: 'characteristic-degree'
  lexiconId: string
  chordIndex: number
}

/**
 * Mode → characteristic-degree configuration (v1).
 *
 * `degreeIndex` is the position in the scale's `intervals` array where the
 * mode's characteristic alteration lives:
 *   - Dorian: position 5 (the ♮6 — distinguishes Dorian from Aeolian's ♭6)
 *   - Mixolydian: position 6 (the ♭7 — distinguishes Mixolydian from Ionian)
 *   - Phrygian: position 1 (the ♭2 — distinguishes Phrygian from Aeolian)
 *   - Lydian: position 3 (the ♯4 — distinguishes Lydian from Ionian)
 *
 * `expectedAlteration` is the interval-string form we expect to see at that
 * position. Reading the intervals BEFORE looking up the note keeps the detector
 * interval-aware (Bowie's invariant: never gate on `scale.type` to decide
 * alteration — gate on the actual interval string). The mode name is used
 * only to pick the lexicon id; the alteration itself is verified against
 * `scale.intervals`. This means a future scale type that happens to share a
 * mode's intervals would be silent until added explicitly here, but no scale
 * type with the wrong intervals can fire under a mode's label.
 */
const MODE_SIGNATURES: Record<
  ModalSignatureHit['mode'],
  { degreeIndex: number; expectedAlteration: string; lexiconId: string }
> = {
  dorian: { degreeIndex: 5, expectedAlteration: 'VI', lexiconId: 'mode-dorian-signature' },
  mixolydian: { degreeIndex: 6, expectedAlteration: 'bVII', lexiconId: 'mode-mixolydian-signature' },
  phrygian: { degreeIndex: 1, expectedAlteration: 'bII', lexiconId: 'mode-phrygian-signature' },
  lydian: { degreeIndex: 3, expectedAlteration: '#IV', lexiconId: 'mode-lydian-signature' },
}

const isSupportedMode = (type: string | undefined): type is ModalSignatureHit['mode'] =>
  type === 'dorian' || type === 'mixolydian' || type === 'phrygian' || type === 'lydian'

/**
 * Detect modal characteristic-note hits across a chord window.
 *
 * Fires when:
 *   - the scale is one of the four v1 modes (Dorian/Mixolydian/Phrygian/Lydian)
 *     verified via `scale.intervals` containing the expected alteration at the
 *     mode's signature degree, AND
 *   - a chord in the window contains the actual pitch class of that signature
 *     note (root, third, fifth, or any upper extension — every chord note is
 *     considered).
 *
 * Returns `[]` when:
 *   - `scale` is undefined,
 *   - `chords` is empty,
 *   - the scale type is not one of the four v1 modes (parent scales like
 *     `major` and `minor` are intentionally silent — S3a is about modes, not
 *     parent scales), or
 *   - no chord in the window contains the signature note.
 *
 * Output is one entry per qualifying chord, in input order. The UI consumer
 * (per-chord glow) routes each entry to its `chordIndex` chip.
 */
export const detectModalSignature = (chords: Chord[], scale: Scale | undefined): ModalSignatureHit[] => {
  if (!scale || chords.length === 0) {
    return []
  }
  if (!isSupportedMode(scale.type)) {
    return []
  }
  const config = MODE_SIGNATURES[scale.type]
  const { intervals } = ScaleLib.getScaleSpec(scale)
  if (intervals[config.degreeIndex] !== config.expectedAlteration) {
    // Defensive: a future scale-spec edit that drops the expected alteration
    // would make the mode-name lookup lie. Silent fail beats false-positive.
    return []
  }
  const scaleNotes = ScaleLib.getNotes(scale)
  const signatureNote = scaleNotes[config.degreeIndex]
  if (!signatureNote) {
    return []
  }
  const signatureIndex = NoteLib.getNoteChromaticIndex(signatureNote)
  const hits: ModalSignatureHit[] = []
  for (let i = 0; i < chords.length; i++) {
    const chord = chords[i]
    const chordNotes = getChordNotes(chord)
    const containsSignature = chordNotes.some(n => NoteLib.getNoteChromaticIndex(n) === signatureIndex)
    if (containsSignature) {
      hits.push({
        mode: scale.type,
        reason: 'characteristic-degree',
        lexiconId: config.lexiconId,
        chordIndex: i,
      })
    }
  }
  return hits
}
